import React, { useState, useRef, useEffect } from 'react';
import { 
  ArrowLeft, Camera, ChevronDown, ChevronUp, Image, Mic, Music, 
  Radio, Sparkles, Upload, Video, Wand2, X
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════════
   CAMERINO — Premium Live Preparation Panel
   
   "Tu camerino antes de salir al escenario"
   - Live camera preview
   - Show customization
   - Clear categories in Spanish
   - Avatar/Banner for the show (not static cover)
═══════════════════════════════════════════════════════════════════════════════ */

const CATEGORIES = [
  { id: 'musica_vivo', icon: Music, label: 'Música en vivo', desc: 'Tocando o cantando' },
  { id: 'dj_set', icon: Radio, label: 'DJ Set', desc: 'Sesión de mezclas' },
  { id: 'produccion', icon: Sparkles, label: 'Producción', desc: 'Creando beats' },
  { id: 'charla', icon: Mic, label: 'Charla', desc: 'Hablando con tu público' },
  { id: 'listening', icon: Music, label: 'Listening Party', desc: 'Escuchando juntos' },
];

/* ── Camera Preview Component ── */
const CameraPreview = ({ isActive, onToggle, lang }) => {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isActive && !stream) {
      navigator.mediaDevices.getUserMedia({ video: true, audio: false })
        .then(s => {
          setStream(s);
          if (videoRef.current) {
            videoRef.current.srcObject = s;
          }
        })
        .catch(err => {
          console.error('Camera error:', err);
          setError(lang === 'es' ? 'No se pudo acceder a la cámara' : 'Could not access camera');
        });
    }
    
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [isActive, stream, lang]);

  return (
    <div className="relative aspect-video rounded-2xl overflow-hidden bg-zinc-900 border border-white/10">
      {isActive && stream ? (
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover scale-x-[-1]"
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
          <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
            <Camera size={28} className="text-white/40" />
          </div>
          {error ? (
            <p className="text-sm text-red-400/80">{error}</p>
          ) : (
            <p className="text-sm text-white/40">
              {lang === 'es' ? 'Tu preview aparecerá aquí' : 'Your preview will appear here'}
            </p>
          )}
        </div>
      )}
      
      {/* Toggle camera button */}
      <button
        onClick={onToggle}
        className={`absolute bottom-3 right-3 px-3 py-1.5 rounded-full text-xs font-medium transition
                   ${isActive 
                     ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30' 
                     : 'bg-white/10 text-white/70 border border-white/10 hover:bg-white/20'}`}
      >
        {isActive ? (lang === 'es' ? 'Apagar' : 'Turn off') : (lang === 'es' ? 'Probar cámara' : 'Test camera')}
      </button>
      
      {/* Live indicator */}
      {isActive && (
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-md">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-[10px] font-medium text-white/90 uppercase tracking-wide">Preview</span>
        </div>
      )}
    </div>
  );
};

/* ── Category Selector ── */
const CategorySelector = ({ value, onChange, lang }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selected = CATEGORIES.find(c => c.id === value) || CATEGORIES[0];

  return (
    <div className="relative">
      <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
        <span className="text-base">🏷️</span>
        {lang === 'es' ? 'Categoría' : 'Category'}
      </label>
      
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                  hover:border-white/20 transition text-left"
      >
        <div className="flex items-center gap-3">
          <selected.icon size={18} className="text-[#d4a853]" />
          <div>
            <p className="text-sm font-medium text-white">{selected.label}</p>
            <p className="text-xs text-white/40">{selected.desc}</p>
          </div>
        </div>
        <ChevronDown size={18} className={`text-white/40 transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 py-2 rounded-xl bg-zinc-900 border border-white/10 shadow-2xl z-50">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => { onChange(cat.id); setIsOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition text-left
                         ${cat.id === value ? 'bg-white/5' : ''}`}
            >
              <cat.icon size={18} className={cat.id === value ? 'text-[#d4a853]' : 'text-white/40'} />
              <div>
                <p className={`text-sm font-medium ${cat.id === value ? 'text-white' : 'text-white/70'}`}>
                  {cat.label}
                </p>
                <p className="text-xs text-white/40">{cat.desc}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Show Avatar/Banner Customizer ── */
const ShowAvatarCustomizer = ({ value, onChange, lang }) => {
  const [mode, setMode] = useState(null); // null, 'upload', 'generate'
  
  return (
    <div>
      <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
        <span className="text-base">🎭</span>
        {lang === 'es' ? 'Avatar de tu show' : 'Show avatar'}
        <span className="text-xs text-white/30 ml-1">(opcional)</span>
      </label>
      
      <div className="flex items-center gap-3">
        {/* Preview */}
        <div className="w-16 h-16 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
          {value ? (
            <img src={value} alt="Avatar" className="w-full h-full object-cover" />
          ) : (
            <Music size={24} className="text-white/20" />
          )}
        </div>
        
        {/* Options */}
        <div className="flex-1 flex flex-wrap gap-2">
          <button
            onClick={() => setMode('upload')}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                      text-xs text-white/70 hover:bg-white/10 transition"
          >
            <Upload size={14} />
            {lang === 'es' ? 'Subir' : 'Upload'}
          </button>
          <button
            onClick={() => onChange(null)} // Use profile photo logic
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 
                      text-xs text-white/70 hover:bg-white/10 transition"
          >
            <Image size={14} />
            {lang === 'es' ? 'Mi foto' : 'My photo'}
          </button>
          <button
            onClick={() => setMode('generate')}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#d4a853]/10 border border-[#d4a853]/30 
                      text-xs text-[#d4a853] hover:bg-[#d4a853]/20 transition"
          >
            <Wand2 size={14} />
            {lang === 'es' ? 'Generar IA' : 'Generate AI'}
          </button>
        </div>
      </div>
      
      {value && (
        <button
          onClick={() => onChange(null)}
          className="mt-2 text-xs text-red-400/60 hover:text-red-400 transition"
        >
          {lang === 'es' ? 'Quitar avatar' : 'Remove avatar'}
        </button>
      )}
    </div>
  );
};

/* ── Advanced Options (Collapsed) ── */
const AdvancedOptions = ({ options, onChange, lang }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-t border-white/5 pt-4 mt-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-sm text-white/40 hover:text-white/60 transition"
      >
        <span className="flex items-center gap-2">
          ⚙️ {lang === 'es' ? 'Opciones avanzadas' : 'Advanced options'}
        </span>
        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      
      {isOpen && (
        <div className="mt-4 space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={options.notifyFollowers}
              onChange={(e) => onChange({ ...options, notifyFollowers: e.target.checked })}
              className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#d4a853] focus:ring-[#d4a853]/50"
            />
            <span className="text-sm text-white/70">
              {lang === 'es' ? 'Notificar a mis seguidores' : 'Notify my followers'}
            </span>
          </label>
          
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={options.followersOnlyChat}
              onChange={(e) => onChange({ ...options, followersOnlyChat: e.target.checked })}
              className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#d4a853] focus:ring-[#d4a853]/50"
            />
            <span className="text-sm text-white/70">
              {lang === 'es' ? 'Chat solo para seguidores' : 'Followers-only chat'}
            </span>
          </label>
        </div>
      )}
    </div>
  );
};

/* ── Main Camerino Panel ── */
export const LiveCamerinoPanel = ({
  session,
  onSave,
  onGoLive,
  onBack,
  lang = 'es',
  isArtist = false,
  busy = false,
}) => {
  const [title, setTitle] = useState(session?.title || '');
  const [description, setDescription] = useState(session?.description || '');
  const [category, setCategory] = useState(session?.category || 'musica_vivo');
  const [avatar, setAvatar] = useState(session?.show_avatar || null);
  const [cameraActive, setCameraActive] = useState(false);
  const [advancedOptions, setAdvancedOptions] = useState({
    notifyFollowers: true,
    followersOnlyChat: false,
  });
  
  const showType = isArtist ? 'Show' : 'Room';
  const showTypeEs = isArtist ? 'Show' : 'Sala';

  const handleSave = () => {
    onSave?.({
      title: title || `${showTypeEs} en vivo`,
      description,
      category,
      show_avatar: avatar,
      ...advancedOptions,
    });
  };

  const handleGoLive = () => {
    handleSave();
    onGoLive?.();
  };

  return (
    <div 
      data-testid="camerino-panel"
      className="min-h-screen bg-[#0a0a0a] text-white"
    >
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0a0a0a]/90 backdrop-blur-lg border-b border-white/5">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={onBack}
            className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/70 hover:bg-white/10 transition"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold flex items-center gap-2">
            🎭 <span>{lang === 'es' ? 'Camerino' : 'Green Room'}</span>
          </h1>
          <div className="w-10" />
        </div>
      </div>

      <div className="px-4 py-6 space-y-6 pb-32">
        {/* Camera Preview */}
        <section>
          <CameraPreview 
            isActive={cameraActive} 
            onToggle={() => setCameraActive(!cameraActive)}
            lang={lang}
          />
          <p className="text-xs text-white/30 mt-2 text-center">
            {lang === 'es' 
              ? 'Comprueba cómo te ves antes de salir al escenario' 
              : 'Check how you look before going on stage'}
          </p>
        </section>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* Title */}
        <div>
          <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
            <span className="text-base">🎤</span>
            {lang === 'es' ? `Nombre de tu ${showTypeEs.toLowerCase()}` : `${showType} name`}
          </label>
          <input
            data-testid="camerino-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={lang === 'es' ? 'Ej: Sesión nocturna de beats' : 'E.g.: Late night beats session'}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white 
                      placeholder-white/30 outline-none focus:border-[#d4a853]/50 transition"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
            <span className="text-base">📝</span>
            {lang === 'es' ? 'Cuéntales de qué va' : 'Tell them what it\'s about'}
          </label>
          <textarea
            data-testid="camerino-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={lang === 'es' 
              ? 'Ej: Vamos a crear música en directo, ¡pide tu canción!' 
              : 'E.g.: Let\'s create music live, request your song!'}
            rows={3}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white 
                      placeholder-white/30 outline-none focus:border-[#d4a853]/50 transition resize-none"
          />
        </div>

        {/* Category */}
        <CategorySelector value={category} onChange={setCategory} lang={lang} />

        {/* Show Avatar */}
        <ShowAvatarCustomizer value={avatar} onChange={setAvatar} lang={lang} />

        {/* Advanced Options */}
        <AdvancedOptions 
          options={advancedOptions} 
          onChange={setAdvancedOptions} 
          lang={lang}
        />
      </div>

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a] to-transparent">
        <div className="space-y-3">
          {session?.status === 'preparing' && (
            <button
              onClick={handleSave}
              disabled={busy}
              className="w-full py-3.5 rounded-xl bg-white/10 border border-white/10 text-white/80 
                        font-medium hover:bg-white/15 transition disabled:opacity-50"
            >
              {lang === 'es' ? 'Guardar cambios' : 'Save changes'}
            </button>
          )}
          
          <button
            data-testid="camerino-go-live"
            onClick={handleGoLive}
            disabled={busy || !title.trim()}
            className="w-full py-4 rounded-xl bg-gradient-to-r from-[#d4a853] to-[#c49843] 
                      text-black font-bold text-lg flex items-center justify-center gap-3
                      hover:from-[#e5b963] hover:to-[#d4a853] transition-all
                      disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#d4a853]/20"
          >
            {busy ? (
              <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
            ) : (
              <>
                <Radio size={20} />
                <span>{lang === 'es' ? 'SALIR AL ESCENARIO' : 'GO ON STAGE'}</span>
              </>
            )}
          </button>
        </div>
        
        <p className="text-xs text-white/30 text-center mt-3">
          {lang === 'es' 
            ? 'Tu directo empezará cuando pulses el botón' 
            : 'Your live will start when you press the button'}
        </p>
      </div>
    </div>
  );
};
